"use client";

import { motion } from "framer-motion";
import { AlertTriangle, Rocket, Coins, Sparkles, ArrowUpCircle, Loader2 } from "lucide-react";
import { useState } from "react";
import PlansModal from "@/components/plans-modal";
import { useUserUsage } from "@/hooks/useUserUsage";
import { UpgradeRequiredCard } from "@/components/upgrade-required-card";
import { apiClient } from "@/lib/api-client";
import { toast } from "sonner";

interface ErrorCardProps {
  errorMessage: string;
  errorType?: string;
  errorSubtype?: string;
}

export function ErrorCard({ errorMessage, errorType, errorSubtype }: ErrorCardProps) {
  const { data: usageData, refreshUsage } = useUserUsage();
  const [isPlansModalOpen, setIsPlansModalOpen] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [isCharging, setIsCharging] = useState(false);

  const is403Error = errorType === 'forbidden';
  const isSessionBusy = errorSubtype === 'session_busy';

  const shouldShowUpgradeButton = is403Error &&
    (errorSubtype === 'no_active_trial' || errorSubtype === 'trial_ended' || errorSubtype === 'subscription_expired' || errorSubtype === 'usage_exceeded' || errorSubtype === 'free_credits_exhausted' || errorSubtype === 'gemini_daily_limit');

  const handleSwitchToGemini = () => {
    // Dismisses this card and auto-resends the blocked message on Gemini.
    window.dispatchEvent(new CustomEvent("xynth:retry-with-model", { detail: { key: "gemini" } }));
    toast.success("Retrying with Gemini, it's free.");
  };


  const handleQuickAddFunds = async () => {
    setIsCharging(true);
    try {
      const result = await apiClient.quickAddFunds(10);
      toast.success(`Added $${result.amount_dollars} — you're good to go!`);
      refreshUsage();
    } catch (err: any) {
      const msg = err?.message || "";
      if (msg.includes("no_saved_method")) {
        const result = await apiClient.addFunds(10);
        window.location.href = result.checkout_url;
      } else if (msg.includes("Card declined") || msg.includes("insufficient")) {
        toast.error("Card declined. Try a different payment method.");
        const result = await apiClient.addFunds(10);
        window.location.href = result.checkout_url;
      } else {
        toast.error("Payment failed. Redirecting to checkout...");
        const result = await apiClient.addFunds(10);
        window.location.href = result.checkout_url;
      }
    } finally {
      setIsCharging(false);
    }
  };

  const handleActivateSubscription = async () => {
    setIsActivating(true);
    try {
      await apiClient.activateSubscription();
      // No page reload: unsent first messages live only client-side (the
      // session isn't persisted until a message passes the usage gate), so a
      // reload on a fresh chat 404s into "Chat not found". Refresh usage in
      // place — same pattern as Add $10 — and let the user resend.
      toast.success("Subscription activated — you're good to go!");
      refreshUsage();
    } catch (err) {
      console.error("Failed to activate subscription:", err);
      toast.error("Failed to activate subscription.");
    } finally {
      setIsActivating(false);
    }
  };

  if (shouldShowUpgradeButton) {
    const trialTier = usageData?.free_trial?.tier
      ? usageData.free_trial.tier.charAt(0).toUpperCase() + usageData.free_trial.tier.slice(1)
      : "Plus";
    const currentTier = usageData?.subscription?.tier || "free";

    let title: React.ReactNode;
    let message: string;
    let actions: { label: string; onClick: () => void; icon?: React.ReactNode; variant: "primary" | "secondary"; disabled?: boolean }[] = [];

    const addFundsLabel = isCharging ? "Adding..." : "Add $10";
    const addFundsIcon = isCharging
      ? <Loader2 className="w-4 h-4 animate-spin" />
      : <Coins className="w-4 h-4" />;
    const isPaidUser = currentTier !== "free";

    if (errorSubtype === 'no_active_trial') {
      title = "Get started with Xynth";
      message = "Start your free trial to unlock premium AI-powered financial analysis.";
      actions = [
        { label: "Begin Free Trial", onClick: () => setIsPlansModalOpen(true), icon: <Rocket className="w-4 h-4" />, variant: "primary" },
      ];
    } else if (errorSubtype === 'trial_ended') {
      title = <>Trial balance exhausted</>;
      message = isPaidUser
        ? `Activate your full ${trialTier} subscription to continue, or add funds.`
        : `Activate your full ${trialTier} subscription to continue.`;
      actions = [
        {
          label: isActivating ? "Activating..." : "Activate Subscription",
          onClick: handleActivateSubscription,
          icon: isActivating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />,
          variant: "primary",
          disabled: isActivating,
        },
        ...(isPaidUser ? [{ label: addFundsLabel, onClick: handleQuickAddFunds, icon: addFundsIcon, variant: "secondary" as const, disabled: isCharging }] : []),
      ];
    } else if (errorSubtype === 'free_credits_exhausted') {
      // Free tier has no included Claude credit (grant removed 2026-09-20).
      // Add $10 charges a saved card instantly or falls through to checkout.
      title = "Claude needs credits";
      message = `Claude models run on credits. Add $10 to use Claude now, upgrade for a monthly allowance, or keep chatting with Gemini for free.`;
      actions = [
        { label: addFundsLabel, onClick: handleQuickAddFunds, icon: addFundsIcon, variant: "primary", disabled: isCharging },
        { label: "Upgrade Plan", onClick: () => setIsPlansModalOpen(true), icon: <ArrowUpCircle className="w-4 h-4" />, variant: "secondary" },
        { label: "Use Gemini free", onClick: handleSwitchToGemini, icon: <Sparkles className="w-4 h-4" />, variant: "secondary" },
      ];
    } else if (errorSubtype === 'gemini_daily_limit') {
      title = "Daily Gemini limit reached";
      message = `You've used today's free Gemini messages. They reset tomorrow, or upgrade for unlimited Gemini.`;
      actions = [
        { label: "Upgrade Plan", onClick: () => setIsPlansModalOpen(true), icon: <ArrowUpCircle className="w-4 h-4" />, variant: "primary" },
      ];
    } else if (errorSubtype === 'usage_exceeded') {
      title = "Monthly Claude credits used up";
      message = isPaidUser
        ? `You've used this month's Claude credits. Add $10 to keep going now, or upgrade for a bigger monthly allowance.`
        : `You've used your Claude credits. Add $10 to keep going, upgrade for monthly credits, or keep chatting with Gemini for free.`;
      actions = [
        ...(currentTier !== "elite" ? [{ label: "Upgrade Plan", onClick: () => setIsPlansModalOpen(true), icon: <ArrowUpCircle className="w-4 h-4" />, variant: "primary" as const }] : []),
        { label: addFundsLabel, onClick: handleQuickAddFunds, icon: addFundsIcon, variant: currentTier === "elite" ? "primary" as const : "secondary" as const, disabled: isCharging },
        ...(isPaidUser
          ? []
          : [{ label: "Use Gemini free", onClick: handleSwitchToGemini, icon: <Sparkles className="w-4 h-4" />, variant: "secondary" as const }]),
      ];
    } else {
      title = "Subscription expired";
      message = "Your subscription has ended. Renew to pick up where you left off.";
      actions = [
        { label: "Renew Subscription", onClick: () => setIsPlansModalOpen(true), icon: <Sparkles className="w-4 h-4" />, variant: "primary" },
      ];
    }

    return (
      <>
        <UpgradeRequiredCard title={title} message={message} actions={actions} />
        <PlansModal
          isOpen={isPlansModalOpen}
          onClose={() => setIsPlansModalOpen(false)}
          currentPlan={(usageData?.subscription?.tier as "free" | "plus" | "pro" | "elite") || "free"}
          stripeAccount={usageData?.stripe_account}
        />
      </>
    );
  }

  const title = isSessionBusy ? "This session is already being worked on" : "Something went wrong";
  const message = isSessionBusy
    ? "Please try again after the current execution finishes in this session."
    : errorMessage;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="w-full max-w-md"
    >
      <div className="rounded-xl border border-red-900/40 bg-zinc-950 px-5 py-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-red-950/50 flex items-center justify-center flex-shrink-0 mt-0.5">
            <AlertTriangle className="w-4 h-4 text-red-400" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-zinc-200 block mb-1">{title}</span>
            <p className="text-sm text-zinc-400 leading-relaxed">{message}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
